import React, {PureComponent} from "react"
import styled from "styled-components"
import {connect} from "react-redux"
import {find} from "lodash"

import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import Button from "@irati/ui/src/Button"
import {H3} from "@irati/ui/src/Headings"
import Card from "@irati/ui/src/Layout/Card"
import {Para} from "@irati/ui/src/Labels"
import {minWidthMedia} from "@irati/ui/src/global/media"
import padding from "@irati/ui/src/global/padding"
import ShowPopUp from "@irati/store/actions/ShowSuccessPopUp"
import onboardingService, {OnboardingStep} from "@irati/onboarding/services/onboarding"
import AuthActions from "@irati/auth/store/actions/AuthActions"
import {User} from "@irati/types"
import TooltipType from "@irati/ui/src/TooltipNotification/types"
import {ReduxState} from "@irati/store/types"

const BannerCTACard = styled(Card)`
  margin: ${padding.base} auto;
  ${minWidthMedia.tablet`
    max-width: 360px;
    margin: 0
  `}
`

const BannerCTAHeading = styled(H3)`
  margin: 0 auto;
  ${minWidthMedia.desktop`
    max-width: 200px;
  `}
`

const BannerCTAPara = styled(Para)`
  margin-bottom: ${padding.base};
`

const Relative = styled.div`
  position: relative;
`

const TooltipAttach = styled.div`
  position: absolute;
  top: -15px;
`

interface Props extends InjectIntl{
  onClick: () => void,
  attachGetStartedTooltip: boolean,
  showPopUp: (tooltip: TooltipType) => void,
  updateAuth: (user: User) => void,
  isMobile: boolean
}

const GETSTARTED = "getStarted"

class CTA extends PureComponent<Props> {
  popupTimeout = null

  componentDidMount() {
    const {isMobile, showPopUp, auth: {onboarding}} = this.props

    window.scrollTo(0, 0)

    const hasViewedMedicalGetStartedPage = find(onboarding.completedSteps, (step) =>
      step === OnboardingStep.HAS_VIEWED_MEDICAL_GET_STARTED_PAGE)
    const hasHiddenMedicalGetStartedTooltip = find(onboarding.completedSteps, (step) =>
      step === OnboardingStep.HAS_HIDDEN_MEDICAL_GET_STARTED_PAGE)

    if (!isMobile) {
      if (hasViewedMedicalGetStartedPage && !hasHiddenMedicalGetStartedTooltip) {
        const {formatMessage} = this.props

        const tooltip = {
          show: true,
          text: formatMessage({id: "medical.landing.tooltip.text"}),
          subtext: formatMessage({id: "medical.tooltip.subtext"}),
          anchor: GETSTARTED,
          positionVertically: true,
          height: 200,
          onChange: () => this.completeStep(OnboardingStep.HAS_HIDDEN_MEDICAL_GET_STARTED_PAGE)
        }
        showPopUp(tooltip)
        this.popupTimeout = setTimeout(() => {
          showPopUp({show: false})
        }, 10000)
      } else {
        this.completeStep(OnboardingStep.HAS_VIEWED_MEDICAL_GET_STARTED_PAGE)
      }
    }
  }

  componentWillUnmount() {
    if (this.popupTimeout) {
      clearTimeout(this.popupTimeout)
    }
    this.props.showPopUp({show: false})
  }

  componentDidUpdate() {
    const {isMobile, showPopUp} = this.props
    if (isMobile) {
      showPopUp({show: false})
    }
  }

  completeStep = (step) => {
    const {auth} = this.props
    onboardingService.completeStep(auth.user, step, auth.accessToken).then((onboarding) => {
      this.props.updateAuth(auth.user, onboarding)
    })
  }

  render() {
    const {onClick, attachGetStartedTooltip} = this.props

    return (
      <BannerCTACard>
        <BannerCTAHeading base>
          <FormattedMessage id="medical.landing.CTA.heading.firstWord" />
          <strong><FormattedMessage id="medical.landing.CTA.heading.bold" /></strong>
          <FormattedMessage id="medical.landing.CTA.heading.sentenceEnd" />
        </BannerCTAHeading>
        <BannerCTAPara light narrow><FormattedMessage id="medical.landing.CTA.para" /></BannerCTAPara>
        <Button full large primary onClick={onClick}>
          <FormattedMessage id="medical.landing.CTA.button" />
        </Button>
        <Relative>
          {attachGetStartedTooltip && <TooltipAttach data-attach-tooltip="getStarted" />}
        </Relative>
      </BannerCTACard>
    )
  }
}

const mapStateToProps = (state: ReduxState) => {
  return {
    isMobile: state.mq.lessThan.large,
    auth: state.auth
  }
}

const mapDispatchToProps = (dispatch:(action: {type: string}) => void) => ({
  showPopUp: (options) => dispatch(ShowPopUp(options)),
  updateAuth: (user, onboarding) => dispatch(AuthActions.UpdateAuthAction({user, onboarding}))
})

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(CTA))
